/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { EmblaOptionsType as CarouselOptionsType, EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import _t from 'i18n!nls/cds-labs';

type CarouselContextType = {
  emblaRef: (node: HTMLElement | null) => void;
  /** When true, scrolls instantly (no animation) — used for keyboard nav so focus moves without waiting for the animation. */
  scrollPrev: (jump?: boolean) => void;
  /** When true, scrolls instantly (no animation) — used for keyboard nav so focus moves without waiting for the animation. */
  scrollNext: (jump?: boolean) => void;
  scrollTo: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  currentPage: number;
  totalPages: number;
  navigationVariant?: 'overlay';
};

const CarouselContext = createContext<CarouselContextType | null>(null);

const styles = {
  root: css`
    width: 100%;
    min-width: 0;
  `,
  rootOverlay: css`
    position: relative;
  `,
};

export const useCarouselContext = (): CarouselContextType => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarouselContext must be used within a Carousel');
  }
  return context;
};

export type { CarouselOptionsType };

/** Details passed to `onNavigate` whenever a prev/next control is activated. */
export type CarouselNavigateInfo = {
  direction: 'previous' | 'next';
  /** The 1-based page the carousel is scrolling to. */
  pageNumber: number;
  totalPages: number;
};

type Props = {
  className?: string;
  options?: CarouselOptionsType;
  navigationVariant?: 'overlay';
  'aria-busy'?: React.AriaAttributes['aria-busy'];
  'aria-live'?: React.AriaAttributes['aria-live'];
  /** Fires when a prev/next navigation control is activated (not on swipe). */
  onNavigate?: (info: CarouselNavigateInfo) => void;
} & ({ 'aria-label': string; 'aria-labelledby'?: never } | { 'aria-label'?: never; 'aria-labelledby': string });

const Carousel = ({
  children,
  className,
  options,
  'aria-busy': ariaBusy,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-live': ariaLive,
  navigationVariant,
  onNavigate,
}: React.PropsWithChildren<Props>) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'keepSnaps',
    ...options,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const updateState = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    setCurrentPage(api.selectedScrollSnap() + 1);
    setTotalPages(api.scrollSnapList().length);
  }, []);

  // Tracks which end of the new page should receive focus after navigation.
  // Set in scrollPrev/scrollNext; consumed in focusOnSelect (jump) or focusAfterSettle (animated).
  const pendingFocus = useRef<{ direction: 'first' | 'last'; jump: boolean; pageInfo: string } | null>(null);

  // Keep only slides in the current snap group focusable so keyboard users don't
  // tab through off-screen cards. We use slideRegistry from internalEngine() rather
  // than position-based checks so the update is instantaneous and animation-timing-safe.
  const updateSlideAccessibility = useCallback<(api: EmblaCarouselType) => void>((api) => {
    const slideNodes = api.slideNodes();
    const snap = api.selectedScrollSnap();
    const { slideRegistry } = api.internalEngine();
    const visibleSet = new Set(slideRegistry[snap] ?? []);
    slideNodes.forEach((slide, i) => {
      if (visibleSet.has(i)) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });
  }, []);

  // Focus the target slide after navigation.
  // Prepends pageInfo to the slide's aria-label so SR reads "Page X of Y. <name>, slide"
  // in one announcement — no timing required. The original label is cached in a data
  // attribute on first call so re-navigation never accumulates stale prefixes, and the
  // approach is locale-safe (no regex matching against translated strings).
  const focusSlide = useCallback((api: EmblaCarouselType, direction: 'first' | 'last', pageInfo: string) => {
    const visibleSlides = api.slideNodes().filter((slide) => !slide.hasAttribute('inert'));
    const targetSlide = direction === 'first' ? visibleSlides[0] : visibleSlides[visibleSlides.length - 1];
    if (!targetSlide) return;

    if (!targetSlide.hasAttribute('data-carousel-base-label')) {
      targetSlide.setAttribute('data-carousel-base-label', targetSlide.getAttribute('aria-label') ?? '');
    }
    const baseLabel = targetSlide.getAttribute('data-carousel-base-label') ?? '';
    targetSlide.setAttribute('aria-label', `${pageInfo}. ${baseLabel}`);
    targetSlide.setAttribute('tabindex', '-1');
    targetSlide.focus({ preventScroll: true });
  }, []);

  // For jump scrolls (keyboard nav): Embla does not fire 'settle' after a jump, so we
  // handle focus on 'select' instead, deferred one frame so slide nodes have been
  // updated. pageInfo is embedded in the slide's aria-label — no timing needed.
  const focusOnSelect = useCallback<(api: EmblaCarouselType) => void>(
    (api) => {
      const pending = pendingFocus.current;
      if (!pending?.jump) return;
      pendingFocus.current = null;
      requestAnimationFrame(() => focusSlide(api, pending.direction, pending.pageInfo));
    },
    [focusSlide]
  );

  // For animated scrolls (mouse nav): Embla fires 'settle' after the animation ends,
  // which is the right moment to move focus without interrupting the scroll animation.
  const focusAfterSettle = useCallback<(api: EmblaCarouselType) => void>(
    (api) => {
      const pending = pendingFocus.current;
      if (!pending || pending.jump) return;
      pendingFocus.current = null;
      focusSlide(api, pending.direction, pending.pageInfo);
    },
    [focusSlide]
  );

  useEffect(() => {
    if (!emblaApi) return () => {};
    updateState(emblaApi);
    updateSlideAccessibility(emblaApi);
    emblaApi.on('select', updateState);
    emblaApi.on('select', updateSlideAccessibility);
    emblaApi.on('select', focusOnSelect);
    emblaApi.on('reInit', updateState);
    emblaApi.on('reInit', updateSlideAccessibility);
    emblaApi.on('settle', focusAfterSettle);
    return () => {
      emblaApi.off('select', updateState);
      emblaApi.off('select', updateSlideAccessibility);
      emblaApi.off('select', focusOnSelect);
      emblaApi.off('reInit', updateState);
      emblaApi.off('reInit', updateSlideAccessibility);
      emblaApi.off('settle', focusAfterSettle);
    };
  }, [emblaApi, updateState, updateSlideAccessibility, focusOnSelect, focusAfterSettle]);

  const scrollPrev = useCallback<(jump?: boolean) => void>(
    (jump) => {
      if (!emblaApi) return;
      const snapCount = emblaApi.scrollSnapList().length;
      const targetIndex = Math.max(0, emblaApi.selectedScrollSnap() - 1);
      const pageInfo =
        snapCount > 1 ? _t('Page #{current} of #{total}', { current: targetIndex + 1, total: snapCount }) : '';
      pendingFocus.current = { direction: 'last', jump: !!jump, pageInfo };
      emblaApi.scrollPrev(jump);
      onNavigate?.({ direction: 'previous', pageNumber: targetIndex + 1, totalPages: snapCount });
    },
    [emblaApi, onNavigate]
  );
  const scrollNext = useCallback<(jump?: boolean) => void>(
    (jump) => {
      if (!emblaApi) return;
      const snapCount = emblaApi.scrollSnapList().length;
      const targetIndex = Math.min(snapCount - 1, emblaApi.selectedScrollSnap() + 1);
      const pageInfo =
        snapCount > 1 ? _t('Page #{current} of #{total}', { current: targetIndex + 1, total: snapCount }) : '';
      pendingFocus.current = { direction: 'first', jump: !!jump, pageInfo };
      emblaApi.scrollNext(jump);
      onNavigate?.({ direction: 'next', pageNumber: targetIndex + 1, totalPages: snapCount });
    },
    [emblaApi, onNavigate]
  );
  const scrollTo = useCallback<(index: number) => void>((index) => emblaApi?.scrollTo(index), [emblaApi]);

  return (
    <CarouselContext.Provider
      value={{
        emblaRef,
        scrollPrev,
        scrollNext,
        scrollTo,
        canScrollPrev,
        canScrollNext,
        currentPage,
        totalPages,
        navigationVariant,
      }}
    >
      <div
        role="region"
        aria-roledescription="carousel"
        aria-busy={ariaBusy}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-live={ariaLive}
        className={className}
        css={[styles.root, navigationVariant === 'overlay' && styles.rootOverlay]}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
};

export default Carousel;
